import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { MobileService } from '@klickdata/core/mobile';
import {
    Resource,
    ResourceCategory,
    ResourceCategoryService,
    ResourceService,
    ResourceSocketService,
    ResourceTag,
    ResourceTypes,
} from '@klickdata/core/resource';
import { ResourceItem, ResourceItemService } from '@klickdata/core/resource-item';
import { TagService } from '@klickdata/core/resource/src/tag/tag.service';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, first, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-resource-details',
    templateUrl: './resource-details.component.html',
    styleUrls: ['./resource-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceDetailsComponent extends OnDestroyHandler implements OnInit {
    @Input() public resourceId: number;
    public resource: Resource;
    public resourceItems$: Observable<ResourceItem[]>;
    public resourceCategories$: Observable<ResourceCategory[]>;
    public resourceTags$: Observable<ResourceTag[]>;
    public user$: Observable<User>;
    public customer$: Observable<Customer>;
    public isMob$: Observable<boolean>;
    ResourceTypes = ResourceTypes;

    constructor(
        protected resourceService: ResourceService,
        protected route: ActivatedRoute,
        protected resourceItemService: ResourceItemService,
        protected categoriesService: ResourceCategoryService,
        protected tagService: TagService,
        protected auth: AuthService,
        protected mobilesSerice: MobileService,
        protected cdr: ChangeDetectorRef,
        protected resourceSocketService: ResourceSocketService
    ) {
        super();
        this.isMob$ = this.mobilesSerice.isMobile();
    }

    ngOnInit() {
        this.user$ = this.auth.getUser().pipe(startWith(new User({ role_value: 'guest' })));
        this.customer$ = this.auth.getCustomer().pipe(first(), startWith(new Customer()));
        this.route.params
            .pipe(
                switchMap((params) =>
                    this.resourceService.getResource(this.resourceId ?? params['id'], [
                        'managers',
                        'publishers',
                        'educators',
                        'hosts',
                        'organizers',
                        'user_enrollment',
                        'user_collaboration',
                        'user_feeds',
                        'editors',
                        'user_competence',
                    ])
                ),
                tap((resource) => this.getResourceData(resource)),
                takeUntil(this.destroy)
            )
            .subscribe((res) => {
                this.resource = res;
                this.cdr.markForCheck();
                this.prepareSocket();
            });
    }
    prepareSocket() {
        this.resourceSocketService
            .listenToResourceUpdates({ res_id: this.resource.id, eager: 'user_enrollment' })
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => (this.resource.user_enrollment = res.user_enrollment));
    }
    getResourceData(resource: Resource) {
        this.resourceItems$ = this.resourceItemService.getOnlyVisibleResourceItems(resource.id);
        this.resourceTags$ = this.tagService.getResourceTags(resource.id, true);
        this.resourceCategories$ = this.categoriesFor(resource);
    }

    /**
     * Guests are not logged in, so there is no customer to resolve a suggested category.
     * That call ends in an empty sequence and used to crash the page. Assigned categories
     * stay visible when the lookup succeeds, and a rejection just hides the row.
     */
    private categoriesFor(resource: Resource): Observable<ResourceCategory[] | null> {
        const assignedCategories$ = this.categoriesService
            .getResourceCategoriesByResourceId(resource.id)
            .pipe(catchError(() => of<ResourceCategory[]>([])));

        return this.auth.getNK3Platform().pipe(
            switchMap((platform) => {
                if (platform === 'guest') {
                    return assignedCategories$.pipe(map((categories) => (categories.length ? categories : null)));
                }

                return combineLatest([
                    assignedCategories$,
                    this.categoriesService
                        .getSuggestedCategory(ResourceTypes.parentType(resource.type_id))
                        .pipe(catchError(() => of<ResourceCategory>(null))),
                ]).pipe(
                    map(([categories, suggestedCategory]) =>
                        suggestedCategory &&
                        Utils.arraysEqual(
                            categories,
                            [suggestedCategory],
                            false,
                            (category, suggested) => category.id === suggested.id
                        )
                            ? null
                            : categories
                    )
                );
            })
        );
    }
}
